import { useMemo, useState } from "react";
import { ResponsiveLine } from "@nivo/line";
import type { LineSeries, PointTooltipProps } from "@nivo/line";
import type { DatumId } from "@nivo/pie";
import { format } from "date-fns";
import { t } from "@/i18n-js/instance";
import { abbreviateNumber } from "@/react/helpers/number";
import { CHART_TYPES } from "@/react/hooks/analytics/chartTypes";
import { useThemeContext } from "@circle-react/providers";
import { ChartContainer } from "../../shared/ChartContainer";
import { EmptyState } from "../../shared/EmptyState";
import { LoadingChartComponent } from "../../shared/LoadingChartComponent";
import { useContainerSize } from "../../shared/hooks/useContainerSize";
import { useTickValues } from "../../shared/hooks/useTickValues";
import type { ChartDataItem } from "../types";
import { baseAxisProps, lineChartTheme } from "./constants";
import { formatLineChartData } from "./utils";

interface LineChartComponentProps {
  className?: string;
  colors: string[];
  curve?:
    | "linear"
    | "cardinal"
    | "catmullRom"
    | "monotoneX"
    | "monotoneY"
    | "natural"
    | "step"
    | "stepAfter"
    | "stepBefore";
  customTooltip?: (
    props: PointTooltipProps<LineSeries>,
  ) => React.ReactElement | null;
  data: ChartDataItem[];
  emptyStateDescription?: string;
  emptyStateTitle?: string;
  enableArea?: boolean;
  enableGridX?: boolean;
  enableGridY?: boolean;
  enablePoints?: boolean;
  indexBy: string;
  isLoading?: boolean;
  prefix?: string;
  rootLocale: string[];
  seriesKeys: string[];
  suffix?: string;
  title: string;
  tooltip?: string;
}

export const LineChartComponent = ({
  isLoading,
  className,
  title,
  data,
  tooltip,
  indexBy,
  seriesKeys,
  colors,
  curve = "monotoneX",
  enableArea = true,
  enableGridX = false,
  enableGridY = true,
  emptyStateTitle,
  emptyStateDescription,
  customTooltip,
  rootLocale,
}: LineChartComponentProps) => {
  const { containerWidth, containerRef } = useContainerSize();
  const [activeId, setActiveId] = useState<DatumId | null>(null);

  const activeColors = seriesKeys.map((key, index) =>
    activeId && activeId !== key ? `${colors[index]}80` : colors[index],
  );

  const { isLightAppearance } = useThemeContext();

  const tickValues = useTickValues({
    data,
    containerWidth,
    indexBy,
  });

  const formattedData = useMemo(
    () => formatLineChartData(data, seriesKeys, indexBy),
    [data, seriesKeys, indexBy],
  );

  if (isLoading) {
    return <LoadingChartComponent type="line" className={className} />;
  }

  if (!data.length) {
    return (
      <EmptyState
        chartTitle={title}
        tooltip={tooltip}
        className={className}
        emptyTitle={emptyStateTitle}
        emptyDescription={emptyStateDescription}
      />
    );
  }

  const theme = lineChartTheme(Boolean(isLightAppearance));

  return (
    <ChartContainer
      title={title}
      tooltip={tooltip}
      className={className}
      data={data}
      chartType={CHART_TYPES.LINE}
    >
      <div className="flex flex-col items-center gap-2">
        <div
          ref={containerRef}
          className="mb-4 flex h-80 w-full overflow-visible"
        >
          <ResponsiveLine
            data={formattedData}
            margin={{ top: 20, right: 20, bottom: 50, left: 60 }}
            xScale={{ type: "point" }}
            yScale={{
              type: "linear",
              min: "auto",
              max: "auto",
              stacked: false,
              reverse: false,
            }}
            curve={curve}
            axisTop={null}
            axisRight={null}
            axisBottom={{
              ...baseAxisProps,
              legend: "Date",
              legendOffset: 40,
              tickValues: tickValues,
              format: (value: string | number | Date) =>
                format(new Date(value), t([...rootLocale, "date_format"])),
            }}
            axisLeft={{
              ...baseAxisProps,
              legend: t([...rootLocale, "conversion_rate"]),
              legendOffset: -50,
              legendPosition: "middle",
              format: abbreviateNumber,
            }}
            enableGridX={enableGridX}
            enableGridY={enableGridY}
            gridYValues={5}
            colors={activeColors}
            enablePoints={false}
            pointSize={0}
            pointColor={{ from: "serieColor" }}
            pointBorderWidth={2}
            pointBorderColor={{ from: "serieColor" }}
            pointLabelYOffset={-12}
            enableArea={enableArea}
            areaOpacity={0.15}
            useMesh
            onMouseEnter={datum => {
              if ("seriesId" in datum) {
                setActiveId(datum.seriesId as DatumId);
              }
            }}
            onMouseLeave={() => setActiveId(null)}
            tooltip={customTooltip}
            theme={theme}
            ariaLabel={t("settings.analytics.line_chart")}
          />
        </div>
      </div>
    </ChartContainer>
  );
};

LineChartComponent.displayName = "LineChartComponent";
